import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { Empty, ErrorBox, PageHead, ResultBadge } from "../components/Bits";
import { Badge, Button, Card, Table } from "../design-system/components";
import { endpoints } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useSession } from "../lib/session";

export function AdminPage() {
  const { isAdmin, setScope } = useSession();
  const users = useQuery({
    queryKey: ["admin", "users"],
    queryFn: endpoints.adminUsers,
    enabled: isAdmin,
  });
  const games = useQuery({
    queryKey: ["admin", "games"],
    queryFn: endpoints.adminGames,
    enabled: isAdmin,
  });
  const [catalogStatus, setCatalogStatus] = useState<string | undefined>(undefined);
  const uploadCatalog = useMutation({
    mutationFn: async (file: File) => endpoints.uploadCatalog(JSON.parse(await file.text())),
    onSuccess: (res) => setCatalogStatus(`Catalog replaced: ${res.cards} cards.`),
    onError: (err) => setCatalogStatus(err instanceof Error ? err.message : String(err)),
  });
  if (!isAdmin) return <ErrorBox error={new Error("Admin only.")} />;
  return (
    <div className="rk-stack">
      <PageHead
        title="Admin"
        subtitle="Everyone who has signed in, and the latest uploads across all players."
      />
      <div className="rk-split rk-split--wide">
        <Card title="Players" flush>
          {users.error && <ErrorBox error={users.error} />}
          {users.data && users.data.users.length === 0 && <Empty title="No players yet" />}
          {users.data && users.data.users.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Handles</th>
                  <th className="num">Games</th>
                  <th>Last upload</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.users.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.displayName}</td>
                    <td className="pr-muted">{u.email}</td>
                    <td>{(u.tcglHandles ?? []).join(", ") || "—"}</td>
                    <td className="num">{u.gamesCount ?? 0}</td>
                    <td className="pr-muted">
                      {u.lastUploadAt ? formatDateTime(u.lastUploadAt) : "—"}
                    </td>
                    <td>
                      <Button size="sm" variant="outline" onClick={() => setScope(u.userId)}>
                        View as
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        <Card
          title="Card catalog"
          description="Replace the client-id → card-name catalog (data/catalog/cards.json from `pnpm catalog:build`)."
        >
          <label className="pr-btn pr-btn--outline" style={{ cursor: "pointer" }}>
            Upload cards.json
            <input
              type="file"
              accept="application/json"
              style={{ display: "none" }}
              onChange={(e) => e.target.files?.[0] && uploadCatalog.mutate(e.target.files[0])}
            />
          </label>
          {catalogStatus && (
            <p className="pr-help" style={{ marginTop: 8 }}>
              {catalogStatus}
            </p>
          )}
        </Card>
      </div>
      <Card title="Latest uploads" flush>
        {games.error && <ErrorBox error={games.error} />}
        {games.data && games.data.games.length === 0 && <Empty title="Nothing uploaded yet" />}
        {games.data && games.data.games.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>Uploaded</th>
                <th>Player</th>
                <th>Game</th>
                <th>Result</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {games.data.games.map((g) => {
                const owner = users.data?.users.find((u) => u.userId === g.userId);
                return (
                  <tr key={`${g.userId}/${g.gameId}`}>
                    <td className="pr-muted">{formatDateTime(g.uploadedAt)}</td>
                    <td>{owner?.displayName ?? g.userId}</td>
                    <td>
                      <Link
                        to={`/games/${g.gameId}?userId=${g.userId}`}
                        onClick={() => setScope(g.userId)}
                      >
                        {g.players.join(" vs ")}
                      </Link>
                    </td>
                    <td>
                      <ResultBadge game={g} />
                    </td>
                    <td>
                      <Badge variant={g.exportVariant === "debug" ? "accent" : "outline"}>
                        {g.exportVariant}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
